import { useCallback, useEffect, useState } from 'react';
import { Loader2, Presentation, MessageSquare } from 'lucide-react';
import api from '../../lib/api';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { type SlideThumbnail } from '../../lib/googleWorkspace';
import { GoogleCommentsImportModal } from './GoogleCommentsImportModal';

interface StoryboardSlidesPanelProps {
  jobId: string;
  documentId: string;
  scriptId?: string;
  readOnly?: boolean;
  kolViewer?: boolean;
  onCommentsImported?: () => void;
}

export function StoryboardSlidesPanel({
  jobId,
  documentId,
  scriptId,
  readOnly = false,
  kolViewer = false,
  onCommentsImported,
}: StoryboardSlidesPanelProps) {
  const [slides, setSlides] = useState<SlideThumbnail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [demo, setDemo] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ slides: SlideThumbnail[]; demo?: boolean }>(
        `/google-workspace/jobs/${jobId}/documents/${documentId}/slides`
      );
      setSlides(res.data.slides ?? []);
      setDemo(Boolean(res.data.demo));
    } catch {
      setError('Failed to load storyboard slides');
    } finally {
      setLoading(false);
    }
  }, [jobId, documentId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <Card className="border-purple-200/60 bg-purple-50/20 dark:bg-purple-950/20">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Presentation className="h-5 w-5 text-purple-600" />
                Storyboard
              </CardTitle>
              <CardDescription>
                {kolViewer
                  ? 'View storyboard slides in kol.my — no Google account needed.'
                  : 'Slide thumbnails from the linked Google Slides deck.'}
              </CardDescription>
            </div>
            {!readOnly && !kolViewer && (
              <Button variant="outline" size="sm" onClick={() => setCommentsOpen(true)}>
                <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                Import comments
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          {!loading && !error && (
            <div className="space-y-3">
              {demo && (
                <Badge variant="secondary">
                  Demo mode — thumbnails require Google OAuth for live decks
                </Badge>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {slides.map((slide) => (
                  <div
                    key={slide.pageId}
                    className="rounded-lg border bg-white dark:bg-black/20 overflow-hidden"
                  >
                    <div className="aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      {slide.thumbnailUrl ? (
                        <img
                          src={slide.thumbnailUrl}
                          alt={slide.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-center p-3">
                          <Presentation className="h-8 w-8 mx-auto text-gray-400 mb-1" />
                          <p className="text-xs text-gray-500">{slide.title}</p>
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {slide.title}
                      </p>
                      {slide.textPreview && (
                        <p className="text-[11px] text-gray-500 line-clamp-2 mt-0.5">
                          {slide.textPreview}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <GoogleCommentsImportModal
        jobId={jobId}
        documentId={documentId}
        scriptId={scriptId}
        isOpen={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        onImported={onCommentsImported}
      />
    </>
  );
}
